import React, { memo } from 'react';
import {
  Button,
  Tag,
  Typography,
  Switch,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CopyOutlined,
  ShoppingOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { WorkOrder } from '../../types';
import { DVIInspectionFull } from '../../types/dvi';
import { copyToClipboard } from '../../utils/clipboard';
import SendDialog from '../Shared/SendDialog';
import { InspectionStatusChip } from '../DVI/InspectionStatusCard';

const { Title, Text } = Typography;

export interface WorkOrderHeaderProps {
  workOrder: WorkOrder;
  isMobile: boolean;
  isLocked: boolean;
  partsNeedingOrderCount: number;
  hasPendingApprovalItems: boolean;
  onNavigateBack: () => void;
  onAddLineItem: () => void;
  /** @deprecated Use SendDialog — kept for existing callers that haven't migrated */
  onSendToCustomer: () => void;
  onPartsModal: () => void;
  onBulkApproval: () => void;
  onUnapprove: () => void;
  onTogglePublicLink: (checked: boolean) => void;
  /** DVI inspection for the glanceable header chip; chip hidden until inspectionLoaded */
  inspection?: DVIInspectionFull | null;
  inspectionLoaded?: boolean;
}

const getPriorityColor = (priority: WorkOrder['priority']) => {
  const colors = { urgent: 'red', high: 'orange', normal: 'blue', low: 'green' };
  return colors[priority] || 'blue';
};

const getStatusColor = (status: WorkOrder['status']) => {
  const colors: Record<string, string> = {
    new: 'blue',
    created: 'blue',
    scheduled: 'cyan',
    in_progress: 'orange',
    waiting_parts: 'purple',
    waiting_approval: 'gold',
    completed: 'green',
    cancelled: 'red',
  };
  return colors[status] || 'default';
};

const WorkOrderHeader: React.FC<WorkOrderHeaderProps> = ({
  workOrder,
  isMobile,
  isLocked,
  partsNeedingOrderCount,
  hasPendingApprovalItems,
  onNavigateBack,
  onAddLineItem,
  onSendToCustomer,
  onPartsModal,
  onBulkApproval,
  onUnapprove,
  onTogglePublicLink,
  inspection,
  inspectionLoaded,
}) => {
  return (
    <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-3 md:py-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={onNavigateBack}
              size={isMobile ? 'middle' : 'large'}
              style={{ height: isMobile ? 36 : 40, width: isMobile ? 36 : 40, flexShrink: 0 }}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center flex-wrap gap-2 mb-1">
                <Title level={isMobile ? 4 : 3} className="!mb-0" style={{ fontSize: isMobile ? 18 : 20 }}>{workOrder.wo_number}</Title>
                <Tag color={getPriorityColor(workOrder.priority)} style={{ fontSize: 11, padding: '1px 8px', margin: 0 }}>
                  {workOrder.priority.toUpperCase()}
                </Tag>
                <Tag color={getStatusColor(workOrder.status)} style={{ fontSize: 11, padding: '1px 8px', margin: 0 }}>
                  {workOrder.status.replace('_', ' ').toUpperCase()}
                </Tag>
                {inspectionLoaded && <InspectionStatusChip inspection={inspection ?? null} />}
              </div>
              <Text type="secondary" style={{ fontSize: 13 }} className="truncate block">
                {workOrder.customer?.company_name ||
                  `${workOrder.customer?.first_name} ${workOrder.customer?.last_name}`}
              </Text>
              {workOrder.estimate_id && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Created from: </Text>
                  <a style={{ fontSize: 12, cursor: 'pointer' }}>
                    View Original Estimate
                  </a>
                </div>
              )}
            </div>
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            width: isMobile ? '100%' : 'auto',
          }}>
            {workOrder.public_link_active && workOrder.public_token && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: isMobile ? '1 1 100%' : 'none' }}>
                <span style={{ fontSize: 12, color: '#666', wordBreak: 'break-all' }}>
                  {window.location.origin}/work-order/{workOrder.public_token}
                </span>
              </div>
            )}
            {workOrder.public_token && (
              <Button
                size="middle"
                type="primary"
                icon={<CopyOutlined />}
                onClick={async () => {
                  const url = `${window.location.origin}/work-order/${workOrder.public_token}`;
                  const success = await copyToClipboard(url);
                  if (success) {
                    message.success('Link copied!');
                  } else {
                    message.error('Failed to copy link');
                  }
                }}
                style={{
                  height: 36,
                  fontSize: 13,
                  fontWeight: 600,
                  flex: isMobile ? '1 1 calc(50% - 4px)' : 'none',
                  minWidth: 0,
                }}
              >
                Copy Link
              </Button>
            )}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, padding: '0 4px' }}>
              <Switch
                size="small"
                checked={workOrder.public_link_active !== false}
                onChange={onTogglePublicLink}
              />
              <span style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                {workOrder.public_link_active !== false ? 'ON' : 'OFF'}
              </span>
            </span>
            {partsNeedingOrderCount > 0 && (
              <Button
                size="middle"
                icon={<ShoppingOutlined />}
                onClick={onPartsModal}
                style={{
                  height: 36,
                  fontSize: 13,
                  fontWeight: 600,
                  borderColor: '#f97316',
                  color: '#f97316',
                  flex: isMobile ? '1 1 calc(50% - 4px)' : 'none',
                  minWidth: 0,
                }}
              >
                Parts ({partsNeedingOrderCount})
              </Button>
            )}
            {hasPendingApprovalItems && (
              <Button
                size="middle"
                onClick={onBulkApproval}
                style={{
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a',
                  color: '#fff',
                  height: 36,
                  fontSize: 13,
                  fontWeight: 600,
                  flex: isMobile ? '1 1 calc(50% - 4px)' : 'none',
                  minWidth: 0,
                }}
              >
                {isMobile ? 'Approve All' : 'Approve All (In-Person)'}
              </Button>
            )}
            {/* C2-T25: SendDialog replaces plain send button */}
            <div style={{ flex: isMobile ? '1 1 calc(50% - 4px)' : 'none', minWidth: 0 }}>
              <SendDialog
                entityType="work-orders"
                entityId={workOrder.id}
                vehicleHasVin={Boolean(workOrder.vehicle?.vin)}
                vehicleHasMileage={Boolean(workOrder.vehicle?.current_mileage)}
                onSent={() => message.success('Work order sent')}
              />
            </div>
            <Button
              size="middle"
              danger
              icon={<DeleteOutlined />}
              onClick={onUnapprove}
              disabled={isLocked}
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                flex: isMobile ? '1 1 calc(50% - 4px)' : 'none',
                minWidth: 0,
              }}
            >
              Unapprove
            </Button>
            <Button
              type="primary"
              size="middle"
              icon={<PlusOutlined />}
              onClick={onAddLineItem}
              disabled={isLocked}
              style={{
                height: 36,
                fontSize: 13,
                fontWeight: 600,
                flex: isMobile ? '1 1 calc(50% - 4px)' : 'none',
                minWidth: 0,
              }}
            >
              {isMobile ? 'Add Line' : 'Add Line Item'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(WorkOrderHeader);
